import { useEffect, useRef, useState } from 'react'
import merchantApi from '../../services/merchantApi'
import './ProfilePage.scss'
import Header from '../../components/Header/Header'
import { Avatar, Grid, Box } from '@mui/material'
import PhoneInputField from '../../components/PhoneInputField/PhoneInputField'
import parsePhoneNumberFromString from 'libphonenumber-js'

const EMPTY_PROFILE = {
  name: '',
  phone: '',
  countryCode: '60',
  email: '',
  role: '',
  joinedDate: '',
  avatar: '',
}

const formatDate = (value = '') => {
  if (!value) return ''

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)

  return date.toLocaleDateString('en-MY', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

const normalizeProfile = (response, currentProfile = EMPTY_PROFILE) => {
  const user = response?.data?.user || response?.data || response?.user || response || {}
  const rawPhone = String(user.phone ?? currentProfile.phone ?? '').replace(/^\+/, '')
  const parsedPhone = parsePhoneNumberFromString(rawPhone.startsWith('60') ? `+${rawPhone}` : '')

  return {
    name: user.name || user.full_name || currentProfile.name || '',
    phone: parsedPhone?.nationalNumber || rawPhone,
    countryCode: parsedPhone?.countryCallingCode || currentProfile.countryCode || '60',
    email: user.email ?? currentProfile.email ?? '',
    role: user.role?.display_name || user.role?.name || user.role_name || currentProfile.role || '',
    joinedDate: formatDate(user.member_since || user.created_at || user.joined_at) || currentProfile.joinedDate || '',
    avatar: user.profile_photo_url || user.profile_photo?.url || user.profile_photo_path || currentProfile.avatar || '',
  }
}

export default function ProfilePage() {
  const [profile, setProfile] = useState(EMPTY_PROFILE)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [selectedPhoto, setSelectedPhoto] = useState(null)
  const [previewUrl, setPreviewUrl] = useState('')
  const [fetchedPhoto, setFetchedPhoto] = useState('')
  const [error, setError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const [fieldError, setFieldError] = useState({})

  const fileInputRef = useRef(null)

  useEffect(() => {
    let active = true

    const loadProfile = async () => {
      try {
        setLoading(true)
        setError('')
        const response = await merchantApi.profile.get()
        if (active) {
          const normalizedProfile = normalizeProfile(response)
          setProfile(normalizedProfile)
          setFetchedPhoto(normalizedProfile.avatar)
        }
      } catch (requestError) {
        if (active) setError(requestError.message || 'Unable to load profile information.')
      } finally {
        if (active) setLoading(false)
      }
    }

    loadProfile()

    return () => {
      active = false
    }
  }, [])

  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl)
  }, [previewUrl])

  const handleUploadPhoto = (event) => {
    const file = event.target.files?.[0]

    if (!file) return
    if (file.size > 2 * 1024 * 1024) {
      setError('Profile photo must be 2MB or smaller.')
      event.target.value = ''
      return
    }

    if (previewUrl) URL.revokeObjectURL(previewUrl)

    const nextPreviewUrl = URL.createObjectURL(file)
    setSelectedPhoto(file)
    setPreviewUrl(nextPreviewUrl)
    setProfile((current) => ({ ...current, avatar: nextPreviewUrl }))
    setError('')
    setSuccessMessage('')

    event.target.value = ''
  }

  const handleRemovePhoto = () => {
    if (!profile.avatar) return

    if (previewUrl) URL.revokeObjectURL(previewUrl)
    setSelectedPhoto(null)
    setPreviewUrl('')
    setProfile((current) => ({ ...current, avatar: '' }))
    setError('')
    setSuccessMessage('')
  }

  const handleProfileUpdate = async () => {
    setSaving(true)
    setError('')
    setSuccessMessage('')
    try {
      const payload = {
        name: profile.name,
        phone: profile?.phone ? profile.countryCode + profile.phone : null,
        email: profile.email,
      }

      const photoChanged = profile.avatar !== fetchedPhoto

      if (photoChanged && fetchedPhoto) {
        await merchantApi.profile.removePhoto()
        setFetchedPhoto('')
      }

      const response = selectedPhoto
        ? await merchantApi.profile.updateMultipart({ ...payload, profile_photo: selectedPhoto })
        : await merchantApi.profile.update(payload)

      const updatedProfile = normalizeProfile(response, profile)
      if (previewUrl) URL.revokeObjectURL(previewUrl)
      setPreviewUrl('')
      setSelectedPhoto(null)
      setProfile(updatedProfile)
      setFetchedPhoto(updatedProfile.avatar)
      setSuccessMessage(response?.message || 'Profile updated successfully.')
      setFieldError({})
      window.location.reload()
    } catch (requestError) {
      setError(requestError.message || 'Unable to update profile.')
      setFieldError(requestError?.data?.errors || {})
    } finally {
      setSaving(false)
    }
  }

  const handleProfileOnChange = (field, e) => {
    setProfile({ ...profile, [field]: e.target.value })
    setSuccessMessage('')
  }

  return (
    <div className="profile-page">
      <Header
        items={[
          { label: 'Cuscapi', to: '/' },
          { label: 'Profile', current: true },
        ]}
      />
      <div className="profile-page__header">
        <div>
          <h1>Profile</h1>
          <p>Manage your personal info, security and notification preferences</p>
        </div>

        <button
          className="profile-page__save"
          onClick={handleProfileUpdate}
          type="button"
          disabled={loading || saving}
        >
          {saving ? 'Saving...' : 'Save changes'}
        </button>
      </div>

      {error && <div className="profile-page__alert profile-page__alert--error">{error}</div>}
      {successMessage && <div className="profile-page__alert profile-page__alert--success" role="status">{successMessage}</div>}

      <div className="">
        <div className="profile-page__main">
          <section className="profile-card">
            <div className="profile-card__header">
              Personal information
            </div>

            <div className="profile-card__body">
              <Grid container spacing={2}>
                <Grid item sm={12}>
                  <Box className="profile-image-upload-container">
                    <div >
                      {
                        profile.avatar ?
                          <Avatar className="profile-page__mui-avatar" src={profile.avatar} alt="Profile photo" />
                          : <Avatar className="profile-page__mui-avatar" />
                      }
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/webp"
                      hidden
                      onChange={handleUploadPhoto}
                      disabled={loading || saving}
                    />
                    <div className="profile-image-upload-container--image-upload">
                      <div className="profile-page__photo-actions">
                        <button type="button" disabled={loading || saving} onClick={() => fileInputRef.current?.click()}>Upload photo</button>
                        <button type="button" disabled={loading || saving || !profile.avatar} className="profile-page__remove" onClick={handleRemovePhoto}>Remove</button>
                      </div>
                      <div className="flex-container">
                        <p>JPG, PNG or WEBP • max 2MB •</p>
                        <p>600x600 recommended</p>
                      </div>
                    </div>
                  </Box>
                </Grid>
                <Grid item md={6} sm={12}>
                  <div className="form-group">
                    <label className="form-label">Name</label>
                    <input className="form-input" value={profile.name} disabled={loading || saving} onChange={(e) => handleProfileOnChange('name', e)} />
                    {fieldError?.name && <div className="profile-page__error-message">{fieldError.name}</div>}
                  </div>
                </Grid>
                <Grid item md={6} sm={12}>
                  <div className="form-group">
                    <label className="form-label">Role</label>
                    <input className="form-input" value={profile.role} readOnly />
                  </div>
                </Grid>
                <Grid item md={6} sm={12}>
                  <div className="form-group">
                    <label className="form-label">Phone number</label>
                    <PhoneInputField
                      value={profile.phone}
                      onChange={(value) => handleProfileOnChange('phone', { target: { value } })}
                      countryCode={profile.countryCode || '60'}
                      onCountryCodeChange={(value) => handleProfileOnChange('countryCode', { target: { value } })}
                      className="profile-page__phone-input"
                      disabled={loading || saving}
                    />
                    {fieldError?.phone && <div className="profile-page__error-message">{fieldError.phone}</div>}
                  </div>
                </Grid>
                <Grid item md={6} sm={12}>
                  <div className="form-group">
                    <label className="form-label">
                      Email address <span className="form-label--optional">(optional)</span>
                    </label>
                    <input className="form-input" placeholder="Email address" value={profile.email} disabled={loading || saving} onChange={(e) => handleProfileOnChange('email', e)} />
                    {fieldError?.email && <div className="profile-page__error-message">{fieldError.email}</div>}
                  </div>
                </Grid>
              </Grid>
            </div>
          </section>
        </div>
      </div>
    </div>
  )
}
